import { useState } from 'react';
import { Link } from 'react-router';
import type { FriendDto, PublicUserDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { Avatar, Empty, ErrorState, Icon, Loading, Segmented, TopBar } from '../components/ui';
import { useT } from '../i18n';
import { errorMessage, useAsync } from '../lib/hooks';
import { shareOrCopy } from '../lib/device';
import { useSession } from '../store/session';
import { toast } from '../store/ui';

interface Requests {
  incoming: { id: string; user: PublicUserDto; createdAt: string }[];
  outgoing: { id: string; user: PublicUserDto; createdAt: string }[];
}

export function Friends() {
  const t = useT();
  const [tab, setTab] = useState<'friends' | 'requests' | 'recent'>('friends');
  const spectate = useSession((s) => s.flag('SPECTATOR_MODE_ENABLED'));
  const [q, setQ] = useState('');
  const friends = useAsync(() => api.get<FriendDto[]>('/api/v1/friends'), []);
  const requests = useAsync(() => api.get<Requests>('/api/v1/friends/requests'), []);
  const recent = useAsync(() => api.get<PublicUserDto[]>('/api/v1/friends/recent'), []);
  const search = useAsync(() => (q.length >= 2 ? api.get<PublicUserDto[]>(`/api/v1/users/search?q=${encodeURIComponent(q)}`) : Promise.resolve([])), [q]);
  const referral = useAsync(() => api.get<{ link: string; code: string }>('/api/v1/referrals'), []);
  const [sent, setSent] = useState<Set<string>>(new Set());

  const add = async (userId: string) => {
    try {
      const r = await api.post<{ status: string }>('/api/v1/friends/requests', { userId });
      setSent(new Set([...sent, userId]));
      toast(r.status === 'ACCEPTED' ? 'You are now friends!' : t('friends.requestSent'), 'success');
      if (r.status === 'ACCEPTED') friends.reload();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };
  const respond = async (id: string, accept: boolean) => {
    try {
      await api.post(`/api/v1/friends/requests/${id}/${accept ? 'accept' : 'reject'}`);
      requests.reload();
      friends.reload();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };
  const friendIds = new Set(friends.data?.map((f) => f.user.id));
  const inviteLink = async () => {
    if (!referral.data) return;
    const r = await shareOrCopy({ title: 'Card Hub', text: `Play cards with me on Card Hub! ${referral.data.link}`, url: referral.data.link });
    if (r === 'copied') toast(t('common.copied'), 'success');
  };

  return (
    <main className="page">
      <TopBar title={t('friends.title')} right={<button className="btn sm primary" onClick={() => void inviteLink()}><Icon name="share" size={16} /> {t('friends.inviteLink')}</button>} />
      <div className="field" style={{ marginBottom: 12 }}>
        <label className="sr-only" htmlFor="fs">{t('friends.search')}</label>
        <input id="fs" className="input" placeholder={`🔍 ${t('friends.search')}`} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {q.length >= 2 && (
        <div className="list" style={{ marginBottom: 16 }}>
          {search.loading && <Loading />}
          {search.data?.length === 0 && <div className="list-item dim">No players found</div>}
          {search.data?.map((u) => (
            <div key={u.id} className="list-item">
              <Avatar avatar={u.avatar} size={40} />
              <Link to={`/profile/${u.id}`} className="grow ellipsis" style={{ color: 'inherit' }}>
                <strong>{u.username}</strong> <span className="muted">{t('common.level', { n: u.level })}</span>
              </Link>
              {friendIds.has(u.id) ? <span className="pill accent">✓</span> : <button className="btn sm" disabled={sent.has(u.id)} onClick={() => void add(u.id)}>{sent.has(u.id) ? '✓' : t('friends.add')}</button>}
            </div>
          ))}
        </div>
      )}
      <Segmented
        label={t('friends.title')}
        value={tab}
        onChange={setTab}
        options={[
          { value: 'friends', label: `${t('friends.title')} ${friends.data ? `(${friends.data.length})` : ''}` },
          { value: 'requests', label: `${t('friends.requests')} ${requests.data?.incoming.length ? `(${requests.data.incoming.length})` : ''}` },
          { value: 'recent', label: t('friends.recent') },
        ]}
      />
      <div style={{ marginTop: 12 }}>
        {tab === 'friends' &&
          (friends.loading ? (
            <Loading />
          ) : friends.error ? (
            <ErrorState message={friends.error} onRetry={friends.reload} />
          ) : friends.data!.length === 0 ? (
            <Empty emoji="🤝" title={t('friends.empty')} action={<button className="btn primary" onClick={() => void inviteLink()}>{t('friends.inviteLink')}</button>} />
          ) : (
            <div className="list">
              {friends.data!.map((f) => (
                <div key={f.user.id} className="list-item">
                  <Link to={`/profile/${f.user.id}`} className="row grow" style={{ color: 'inherit', gap: 12, minWidth: 0 }}>
                    <Avatar avatar={f.user.avatar} size={44} status={f.status} />
                    <span className="grow ellipsis">
                      <strong>{f.user.username}</strong>
                      <br />
                      <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t(`friends.status.${f.status}`)}</span>
                    </span>
                  </Link>
                  {f.tableId && spectate ? (
                    <Link to={`/watch/${f.tableId}`} className="btn sm" aria-label={`${t('watch.cta')} ${f.user.username}`}>
                      👁 {t('watch.cta')}
                    </Link>
                  ) : (
                    <span className="pill">{t('common.level', { n: f.user.level })}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        {tab === 'requests' &&
          (requests.loading ? (
            <Loading />
          ) : requests.data && requests.data.incoming.length + requests.data.outgoing.length === 0 ? (
            <Empty emoji="📭" title="No pending requests" />
          ) : (
            <div className="list">
              {requests.data?.incoming.map((r) => (
                <div key={r.id} className="list-item">
                  <Avatar avatar={r.user.avatar} size={40} />
                  <strong className="grow ellipsis">{r.user.username}</strong>
                  <button className="btn sm primary" onClick={() => void respond(r.id, true)}>{t('friends.accept')}</button>
                  <button className="btn sm ghost" onClick={() => void respond(r.id, false)}>{t('friends.reject')}</button>
                </div>
              ))}
              {requests.data?.outgoing.map((r) => (
                <div key={r.id} className="list-item">
                  <Avatar avatar={r.user.avatar} size={40} />
                  <span className="grow ellipsis">{r.user.username}</span>
                  <span className="pill">{t('friends.requestSent')}</span>
                </div>
              ))}
            </div>
          ))}
        {tab === 'recent' &&
          (recent.loading ? (
            <Loading />
          ) : recent.data?.length === 0 ? (
            <Empty emoji="🕒" title="Play a game with others to see them here" />
          ) : (
            <div className="list">
              {recent.data?.map((u) => (
                <div key={u.id} className="list-item">
                  <Avatar avatar={u.avatar} size={40} />
                  <Link to={`/profile/${u.id}`} className="grow ellipsis" style={{ color: 'inherit' }}>{u.username}</Link>
                  {!friendIds.has(u.id) && <button className="btn sm" disabled={sent.has(u.id)} onClick={() => void add(u.id)}>{t('friends.add')}</button>}
                </div>
              ))}
            </div>
          ))}
      </div>
    </main>
  );
}
